import {ChevronRight} from 'lucide-react';
import * as React from 'react';
import {useLocation} from 'react-router-dom';
import {MenuButton} from 'src/SideNav/MenuItem';

type Props = {
  label: string;
  /**
   * Paths of the links inside the section. While one of them is the current
   * page the section renders open regardless of the stored choice, so the
   * active item is never hidden behind a collapsed header.
   */
  childPaths: string[];
  /** localStorage key persisting the open/closed choice across reloads. */
  storageKey: string;
  children: React.ReactNode;
};

const loadStored = (key: string): boolean | null => {
  try {
    const raw = window.localStorage.getItem(key);
    return raw === null ? null : raw === 'true';
  } catch {
    // Storage unavailable — fall back to route-derived state
    return null;
  }
};

/**
 * A collapsible sidebar group for secondary destinations, so they don't
 * take permanent slots in the nav. The stored choice only applies away
 * from the section's own pages: a child route being active forces the
 * section open, since collapsing it would hide the current page's link.
 */
export const ExpandableMenuSection = (props: Props) => {
  const {label, childPaths, storageKey, children} = props;
  const location = useLocation();
  const contentId = React.useId();
  const childActive = childPaths.some((path) =>
    location.pathname.startsWith(path),
  );

  const [open, setOpen] = React.useState<boolean>(
    () => loadStored(storageKey) ?? false,
  );
  const effectiveOpen = open || childActive;

  const handleToggle = () => {
    setOpen((prev) => {
      const next = !prev;
      try {
        window.localStorage.setItem(storageKey, String(next));
      } catch {
        // Storage unavailable — state still works for this session
      }
      return next;
    });
  };

  return (
    <>
      <MenuButton
        aria-expanded={effectiveOpen}
        aria-controls={contentId}
        iconLeft={
          <ChevronRight
            className="transition-transform duration-100"
            style={{transform: effectiveOpen ? 'rotate(90deg)' : 'rotate(0deg)'}}
          />
        }
        label={label}
        onClick={handleToggle}
      />
      {effectiveOpen ? (
        <div id={contentId} className="flex flex-col px-2">
          {children}
        </div>
      ) : null}
    </>
  );
};
